import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { isWhiteLabel } from "../../services/agencyService";
import { RefreshCw, Save, Calendar, ArrowLeft, Plus, Mic, MicOff, Sparkles, ChevronDown, ChevronRight, ChevronLeft } from "lucide-react";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import selfInterviewService, { SelfInterview as SelfInterviewType, SelfInterviewQuestion } from "../../services/selfInterviewService";
import onboardingService from "../../services/onboardingService";
import strategyDocumentService from "../../services/strategyDocumentService";
import QuestionCard from "./QuestionCard";

type Step = "setup" | "questions" | "review";

const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAY_CODES: Record<string, string> = {
  Monday: "MO", Tuesday: "TU", Wednesday: "WE", Thursday: "TH",
  Friday: "FR", Saturday: "SA", Sunday: "SU",
};

const SelfInterview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState<Step>("setup");
  const [interview, setInterview] = useState<SelfInterviewType | null>(null);
  const [questions, setQuestions] = useState<SelfInterviewQuestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [blueprints, setBlueprints] = useState<any[]>([]);
  const [selectedBlueprintId, setSelectedBlueprintId] = useState<number | null>(null);
  const [showBlueprintSelector, setShowBlueprintSelector] = useState(false);
  const [swappingIndex, setSwappingIndex] = useState<number | null>(null);
  const [activePillarIndex, setActivePillarIndex] = useState<Record<string, number>>({});
  const [retryingNews, setRetryingNews] = useState(false);

  // Strategy documents
  const [strategyDocs, setStrategyDocs] = useState<Array<{ id: number; name: string; audience: string | null; google_doc_url: string | null; status: string; created_at: string }>>([]);
  const [selectedStrategyDocId, setSelectedStrategyDocId] = useState<number | null>(null);
  const [googleDocUrl, setGoogleDocUrl] = useState("");
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [docLoaded, setDocLoaded] = useState(false);
  const [showGoogleDocUrl, setShowGoogleDocUrl] = useState(false);
  const [needsSharing, setNeedsSharing] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<{ ingredients_created: number } | null>(null);

  // Calendar
  const [calendarDay, setCalendarDay] = useState("Tuesday");
  const [calendarTime, setCalendarTime] = useState("10:00");

  // Custom topic input
  const [customTopicInput, setCustomTopicInput] = useState("");
  const [addingTopic, setAddingTopic] = useState(false);
  const [topicMicActive, setTopicMicActive] = useState(false);
  const [showAddTopic, setShowAddTopic] = useState(false);
  const [savedNotice, setSavedNotice] = useState("");

  const [sharpeningIndex, setSharpeningIndex] = useState<number | null>(null);

  // Voice for custom topic
  const { transcript, listening, resetTranscript, browserSupportsSpeechRecognition } = useSpeechRecognition();

  // Past sessions
  const [pastSessions, setPastSessions] = useState<SelfInterviewType[]>([]);
  const [expandedSession, setExpandedSession] = useState<number | null>(null);

  // Build email
  const [buildingEmail, setBuildingEmail] = useState<number | null>(null);

  useEffect(() => {
    loadStrategyDocs();
    loadBlueprints();
    loadPastSessions();
    if (id) loadInterview(parseInt(id));
  }, [id]);

  useEffect(() => {
    if (transcript && topicMicActive) setCustomTopicInput(transcript);
  }, [transcript, topicMicActive]);

  // Autosave: debounce persisting questions 2s after the last change
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstRender = useRef(true);
  useEffect(() => {
    // Skip the initial load (questions populated from the server)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!interview || currentStep !== "questions" || questions.length === 0) return;

    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      selfInterviewService.update(interview.id, { questions }).catch(console.error);
    }, 2000);

    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [questions]);

  const loadStrategyDocs = async () => {
    try {
      const data = await strategyDocumentService.list();
      setStrategyDocs(data);
      if (data.length > 0) {
        setSelectedStrategyDocId(data[0].id);
        if (data[0].google_doc_url) setGoogleDocUrl(data[0].google_doc_url);
      }
    } catch (err) {
      console.error("Failed to load strategy docs:", err);
    }
  };

  const loadBlueprints = async () => {
    try {
      const data = await onboardingService.listOnboardingBlueprints();
      setBlueprints(data);
      if (data.length > 0 && !selectedBlueprintId) {
        // Use primary blueprint if set, otherwise first
        try {
          const { getPrimaryBlueprint } = await import("../../services/organizationService");
          const primary = await getPrimaryBlueprint();
          if (primary.id && data.some((bp: any) => bp.id === primary.id)) {
            setSelectedBlueprintId(primary.id);
          } else {
            setSelectedBlueprintId(data[0].id);
          }
        } catch {
          setSelectedBlueprintId(data[0].id);
        }
      }
    } catch (err) {
      console.error("Failed to load blueprints:", err);
    }
  };

  const loadPastSessions = async () => {
    try {
      const data = await selfInterviewService.list();
      setPastSessions(data.filter((s) => s.status === "Completed" || s.status === "Saved"));
    } catch (err) {
      console.error("Failed to load past sessions:", err);
    }
  };

  const loadInterview = async (interviewId: number) => {
    try {
      const data = await selfInterviewService.show(interviewId);
      setInterview(data);
      setQuestions(data.questions || []);
      if (data.status === "Completed") {
        setCurrentStep("review");
      } else if (data.status === "Saved") {
        setCurrentStep("questions");
      } else {
        setCurrentStep("questions");
      }
    } catch (err) {
      console.error("Failed to load interview:", err);
    }
  };

  // Build params for question generation based on current selection
  const getGenerationParams = () => {
    const params: { strategyDocumentId?: number; blueprintId?: number } = {};
    if (selectedStrategyDocId) params.strategyDocumentId = selectedStrategyDocId;
    if (selectedBlueprintId) params.blueprintId = selectedBlueprintId;
    return params;
  };

  const [loadingStep, setLoadingStep] = useState("");

  const SERVICE_ACCOUNT_EMAIL = "potions-docs-reader@calude-490602.iam.gserviceaccount.com";

  const handleLoadDoc = async () => {
    if (!selectedStrategyDocId || !googleDocUrl) return;
    setLoadingDoc(true);
    setDocLoaded(false);
    setNeedsSharing(false);
    setError("");
    try {
      await strategyDocumentService.refreshFromGoogle(selectedStrategyDocId, googleDocUrl);
      setDocLoaded(true);
      setNeedsSharing(false);
      // Update the local list so the URL is reflected
      setStrategyDocs((prev) =>
        prev.map((d) => d.id === selectedStrategyDocId ? { ...d, google_doc_url: googleDocUrl } : d)
      );
    } catch (err: any) {
      const msg = err?.response?.data?.error || "";
      const status = err?.response?.status;
      if (msg === "permission_denied" || status === 403 || msg.includes("permission") || msg.includes("Invalid request")) {
        setNeedsSharing(true);
      } else {
        setError(msg || "Failed to load Google Doc");
      }
    } finally {
      setLoadingDoc(false);
    }
  };

  const loadingMessages = [
    "Reading your blueprint...",
    "Analyzing your audience and ICP...",
    "Researching recent industry news...",
    "Searching for trending articles...",
    "Crafting personalized questions...",
    "Reviewing your content history...",
    "Finalizing your interview session...",
  ];

  const handleStart = async () => {
    setLoading(true);
    setError("");
    setLoadingStep(loadingMessages[0]);

    let msgIndex = 0;
    const msgInterval = setInterval(() => {
      msgIndex = (msgIndex + 1) % loadingMessages.length;
      setLoadingStep(loadingMessages[msgIndex]);
    }, 4000);

    try {
      if (selectedStrategyDocId && googleDocUrl && !docLoaded) {
        const selectedDoc = strategyDocs.find((d) => d.id === selectedStrategyDocId);
        if (googleDocUrl !== selectedDoc?.google_doc_url) {
          await strategyDocumentService.refreshFromGoogle(selectedStrategyDocId, googleDocUrl);
        }
      }

      const newInterview = await selfInterviewService.create();
      setInterview(newInterview);

      // Kick off async generation
      await selfInterviewService.generateQuestionsAsync(newInterview.id, getGenerationParams());

      // Poll until questions are populated (up to 5 minutes)
      const maxAttempts = 60;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise((r) => setTimeout(r, 5000));
        const updated = await selfInterviewService.show(newInterview.id);
        if (updated.questions && updated.questions.length > 0) {
          // Check for error marker
          if (updated.questions[0] && (updated.questions[0] as any).error) {
            throw new Error("Question generation failed. Please try again.");
          }
          setInterview(updated);
          setQuestions(updated.questions.map((q) => ({
            ...q,
            pillar: q.pillar || q.category || "business",
          })));
          clearInterval(msgInterval);
          setLoadingStep("");
          setCurrentStep("questions");
          setLoading(false);
          return;
        }
      }

      throw new Error("Question generation timed out. Please try again.");
    } catch (err: any) {
      clearInterval(msgInterval);
      setError(err?.message || err?.response?.data?.error || "Failed to start session");
      setLoadingStep("");
      setLoading(false);
    }
  };

  const handleRefreshAll = async () => {
    if (!interview) return;
    setLoading(true);
    setLoadingStep("Regenerating all questions...");
    try {
      // Clear existing questions so polling can detect when new ones arrive
      await selfInterviewService.update(interview.id, { questions: [] });
      await selfInterviewService.generateQuestionsAsync(interview.id, getGenerationParams());

      for (let attempt = 0; attempt < 60; attempt++) {
        await new Promise((r) => setTimeout(r, 5000));
        const updated = await selfInterviewService.show(interview.id);
        if (updated.questions && updated.questions.length > 0) {
          if (updated.questions[0] && (updated.questions[0] as any).error) {
            throw new Error("Question generation failed. Please try again.");
          }
          setInterview(updated);
          setQuestions(updated.questions.map((q) => ({
            ...q,
            pillar: q.pillar || q.category || "business",
          })));
          setLoadingStep("");
          setLoading(false);
          return;
        }
      }
      throw new Error("Question generation timed out. Please try again.");
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to refresh questions");
      setLoadingStep("");
      setLoading(false);
    }
  };

  const handleDismiss = (index: number) => {
    setQuestions((prev) =>
      prev.map((q, i) => (i === index ? { ...q, dismissed: true } : q))
    );
  };

  const handleSwap = async (index: number) => {
    setSwappingIndex(index);
    try {
      const replacement = await selfInterviewService.swapQuestion(
        questions[index].question,
        { ...getGenerationParams(), source: questions[index].source }
      );
      setQuestions((prev) =>
        prev.map((q, i) =>
          i === index
            ? { ...replacement, pillar: replacement.pillar || q.pillar, answer_text: "", answer_duration: 0, quality_score: null, quality_feedback: null, dismissed: false }
            : q
        )
      );
    } catch (err) {
      console.error("Failed to swap question:", err);
    } finally {
      setSwappingIndex(null);
    }
  };

  const handleAnswer = (index: number, answerText: string, duration?: number) => {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === index ? { ...q, answer_text: answerText, answer_duration: duration || q.answer_duration } : q
      )
    );
  };

  const handleDone = (index: number) => {
    // Auto-save incrementally
    if (interview) {
      selfInterviewService.update(interview.id, { questions }).catch(console.error);
    }
  };

  const handleAssess = async (index: number) => {
    if (!interview) return;
    const q = questions[index];
    if (!q.answer_text) return;
    // Clear existing score to show loading spinner
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === index ? { ...q, quality_score: null, quality_feedback: null, quality_suggestions: [] } : q
      )
    );
    try {
      const result = await selfInterviewService.assessAnswer(interview.id, q.question, q.answer_text);
      setQuestions((prev) =>
        prev.map((q, i) =>
          i === index ? { ...q, quality_score: result.score, quality_feedback: result.feedback, quality_suggestions: result.suggestions || [] } : q
        )
      );
    } catch (err) {
      console.error("Failed to assess answer:", err);
    }
  };

  const handleSharpen = async (index: number) => {
    if (!interview) return;
    const q = questions[index];
    if (!q.answer_text) return;
    setSharpeningIndex(index);
    try {
      const result = await selfInterviewService.sharpenAnswer(interview.id, q.question, q.answer_text);
      setQuestions((prev) =>
        prev.map((q, i) =>
          i === index ? { ...q, answer_text: result.improved_text, quality_score: null, quality_feedback: null, quality_suggestions: [] } : q
        )
      );
      // Re-assess after sharpening
      handleAssess(index);
    } catch (err) {
      console.error("Failed to sharpen answer:", err);
    } finally {
      setSharpeningIndex(null);
    }
  };

  const handleRetryNews = async () => {
    if (!interview) return;
    setRetryingNews(true);
    try {
      const questionCountBefore = interview.questions?.length || 0;
      await selfInterviewService.generateQuestionsAsync(interview.id, { ...getGenerationParams(), newsOnly: true });

      for (let attempt = 0; attempt < 60; attempt++) {
        await new Promise((r) => setTimeout(r, 5000));
        const updated = await selfInterviewService.show(interview.id);
        if (updated.questions && updated.questions.length > questionCountBefore) {
          if (updated.questions[0] && (updated.questions[0] as any).error) {
            throw new Error("News search failed. Please try again.");
          }
          setInterview(updated);
          setQuestions(updated.questions.map((q) => ({
            ...q,
            pillar: q.pillar || q.category || "business",
          })));
          setRetryingNews(false);
          return;
        }
      }
      throw new Error("News search timed out. Please try again.");
    } catch (err) {
      console.error("Failed to retry news search:", err);
    } finally {
      setRetryingNews(false);
    }
  };

  const handleAddCustomTopic = () => {
    if (!customTopicInput.trim()) return;
    setQuestions((prev) => [
      ...prev,
      {
        question: customTopicInput.trim(),
        category: "custom",
        pillar: "custom" as any,
        source: "custom" as const,
        answer_text: "",
        answer_duration: 0,
        quality_score: null,
        quality_feedback: null,
        dismissed: false,
      },
    ]);
    setCustomTopicInput("");
    resetTranscript();
  };

  const handleSaveDraft = async () => {
    if (!interview) return;
    setSaving(true);
    setError("");
    try {
      await selfInterviewService.saveSession(interview.id, { questions });
      setInterview({ ...interview, status: "Saved" });
      setError("");
      setSavedNotice("Session saved.");
      setTimeout(() => setSavedNotice(""), 3000);
      loadPastSessions();
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to save session");
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteSession = async () => {
    if (!interview) return;
    setSaving(true);
    setError("");
    try {
      await selfInterviewService.update(interview.id, { questions });
      const result = await selfInterviewService.saveSession(interview.id, { complete: true });
      setSaveResult({ ingredients_created: result.ingredients_created || 0 });
      setCurrentStep("review");
      loadPastSessions();
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to complete session");
    } finally {
      setSaving(false);
    }
  };

  const handleBuildEmail = async (index: number) => {
    if (!interview || !selectedBlueprintId) return;
    setBuildingEmail(index);
    try {
      const result = await selfInterviewService.buildEmail(interview.id, index, selectedBlueprintId);
      window.open(result.magic_link, "_blank");
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to build email");
    } finally {
      setBuildingEmail(null);
    }
  };

  const generateCalendarUrl = () => {
    const dayCode = DAY_CODES[calendarDay] || "TU";
    const [hours, minutes] = calendarTime.split(":").map(Number);
    const now = new Date();
    // Find next occurrence of selected day
    const dayIndex = DAYS_OF_WEEK.indexOf(calendarDay);
    const currentDay = now.getDay();
    const targetDay = dayIndex === 6 ? 0 : dayIndex + 1; // Convert to JS day (0=Sun)
    let daysUntil = targetDay - currentDay;
    if (daysUntil <= 0) daysUntil += 7;
    const startDate = new Date(now);
    startDate.setDate(now.getDate() + daysUntil);
    startDate.setHours(hours, minutes, 0, 0);
    const endDate = new Date(startDate);
    endDate.setMinutes(endDate.getMinutes() + 15);

    const fmt = (d: Date) =>
      d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");

    const dashboardUrl = window.location.origin + "/self-interviews";
    const params = new URLSearchParams({
      text: isWhiteLabel() ? "15-minute content session" : "Potions: 15-minute content session",
      dates: `${fmt(startDate)}/${fmt(endDate)}`,
      recur: `RRULE:FREQ=WEEKLY;BYDAY=${dayCode}`,
      details: `Time to capture your expertise! Open your ${isWhiteLabel() ? "" : "Potions "}self-interview:\n${dashboardUrl}`,
    });

    return `https://calendar.google.com/calendar/r/eventedit?${params.toString()}`;
  };

  const answeredCount = questions.filter((q) => q.answer_text && !q.dismissed).length;
  const activeCount = questions.filter((q) => !q.dismissed).length;

  // ---- Render ----

  const renderSetup = () => (
    <div style={{ maxWidth: "640px", margin: "0 auto", padding: "40px 20px" }}>
      <h1 style={{ fontSize: "28px", fontWeight: 700, marginBottom: "8px", color: "#111827" }}>
        AI Self-Interview
      </h1>
      <p style={{ fontSize: "15px", color: "#6B7280", lineHeight: 1.7, marginBottom: "32px" }}>
        Answer AI-generated questions to create original content for your newsletter.
        Takes about <strong>15 minutes</strong> — just talk naturally and we'll turn your answers into content for your newsletter.
      </p>

      {/* Strategy document selector */}
      <div style={{ marginBottom: "24px" }}>
        <label style={{ fontSize: "14px", fontWeight: 600, color: "#374151", display: "block", marginBottom: "4px" }}>
          Strategy Document (optional)
        </label>
        <p style={{ fontSize: "13px", color: "#9CA3AF", marginBottom: "8px", lineHeight: 1.4 }}>
          Your strategy document defines your audience, tone, and content plan. Selecting one helps generate more relevant questions.
        </p>
        <select
          value={selectedStrategyDocId || ""}
          onChange={(e) => {
            const val = e.target.value ? Number(e.target.value) : null;
            setSelectedStrategyDocId(val);
            const doc = strategyDocs.find((d) => d.id === val);
            setGoogleDocUrl(doc?.google_doc_url || "");
          }}
          style={{
            width: "100%", padding: "10px 14px", borderRadius: "8px",
            border: "1px solid #D1D5DB", fontSize: "14px", outline: "none",
            backgroundColor: "#FFFFFF",
          }}
        >
          <option value="">No strategy document — general questions</option>
          {strategyDocs.map((doc) => (
            <option key={doc.id} value={doc.id}>
              {doc.name}
            </option>
          ))}
        </select>
        <div style={{ display: "flex", gap: "12px", marginTop: "6px", flexWrap: "wrap" }}>
          {selectedStrategyDocId && googleDocUrl && (
            <a
              href={googleDocUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: "12px", color: "#6366F1", textDecoration: "none" }}
            >
              Open in Google Docs
            </a>
          )}
          <a
            href="/blueprints/import-strategy"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: "12px", color: "#6366F1", textDecoration: "none" }}
          >
            Import a strategy document
          </a>
        </div>
      </div>

      {/* Google Doc URL field — hidden by default if they already have a strategy doc */}
      {selectedStrategyDocId && !showGoogleDocUrl && (
        <div style={{ marginBottom: "24px" }}>
          <button
            onClick={() => setShowGoogleDocUrl(true)}
            style={{
              background: "none", border: "none", padding: 0, cursor: "pointer",
              fontSize: "12px", color: "#6366F1",
            }}
          >
            Update strategy document from Google Docs
          </button>
        </div>
      )}
      {selectedStrategyDocId && showGoogleDocUrl && (
        <div style={{ marginBottom: "24px" }}>
          <label style={{ fontSize: "13px", fontWeight: 500, color: "#374151", display: "block", marginBottom: "6px" }}>
            Google Doc URL (paste to refresh content from Google Docs)
          </label>
          <div style={{ display: "flex", gap: "8px" }}>
            <input
              type="text"
              value={googleDocUrl}
              onChange={(e) => { setGoogleDocUrl(e.target.value); setDocLoaded(false); }}
              placeholder="https://docs.google.com/document/d/..."
              style={{
                flex: 1, padding: "10px 14px", borderRadius: "8px",
                border: `1px solid ${docLoaded ? "#BBF7D0" : "#D1D5DB"}`, fontSize: "14px", outline: "none",
              }}
            />
            <button
              onClick={handleLoadDoc}
              disabled={loadingDoc || !googleDocUrl.includes("docs.google.com")}
              style={{
                padding: "10px 16px", borderRadius: "8px",
                backgroundColor: docLoaded ? "#F0FDF4" : "#6366F1",
                color: docLoaded ? "#16A34A" : "#FFFFFF",
                border: docLoaded ? "1px solid #BBF7D0" : "none",
                fontSize: "13px", fontWeight: 600, whiteSpace: "nowrap",
                cursor: loadingDoc || !googleDocUrl.includes("docs.google.com") ? "not-allowed" : "pointer",
                opacity: loadingDoc || !googleDocUrl.includes("docs.google.com") ? 0.6 : 1,
              }}
            >
              {loadingDoc ? "Loading..." : docLoaded ? "Loaded" : "Load Doc"}
            </button>
          </div>
          {docLoaded && (
            <p style={{ fontSize: "12px", color: "#16A34A", marginTop: "4px" }}>
              Strategy document refreshed from Google Docs.
            </p>
          )}
          {needsSharing && (
            <div style={{
              marginTop: "8px", padding: "12px 16px", borderRadius: "8px",
              backgroundColor: "#FFFBEB", border: "1px solid #FDE68A",
            }}>
              <p style={{ fontSize: "13px", color: "#92400E", marginBottom: "8px", fontWeight: 600 }}>
                {isWhiteLabel() ? "We don't" : "Potions doesn't"} have access to this doc yet.
              </p>
              <p style={{ fontSize: "13px", color: "#92400E", marginBottom: "8px" }}>
                Share the Google Doc with this email as a <strong>Viewer</strong>, then click "Load Doc" again:
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <code style={{
                  fontSize: "12px", backgroundColor: "#FEF3C7", padding: "6px 10px",
                  borderRadius: "6px", border: "1px solid #FDE68A", color: "#78350F",
                  userSelect: "all", flex: 1,
                }}>
                  {SERVICE_ACCOUNT_EMAIL}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(SERVICE_ACCOUNT_EMAIL);
                    setEmailCopied(true);
                    setTimeout(() => setEmailCopied(false), 2000);
                  }}
                  style={{
                    padding: "6px 12px", borderRadius: "6px",
                    border: "1px solid #FDE68A", backgroundColor: emailCopied ? "#D9F99D" : "#FFFFFF",
                    fontSize: "12px", color: emailCopied ? "#166534" : "#78350F",
                    cursor: "pointer", whiteSpace: "nowrap", fontWeight: 500,
                  }}
                >
                  {emailCopied ? "Copied!" : "Copy"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Optional blueprint selector — only show if no strategy docs exist */}
      {strategyDocs.length === 0 && (
        <div style={{ marginBottom: "24px" }}>
          <button
            onClick={() => setShowBlueprintSelector(!showBlueprintSelector)}
            style={{
              background: "none", border: "none", padding: 0, cursor: "pointer",
              fontSize: "13px", color: "#6B7280", display: "flex", alignItems: "center", gap: "4px",
            }}
          >
            {showBlueprintSelector ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            Or use a blueprint instead
          </button>
          {showBlueprintSelector && (
            <div style={{ marginTop: "8px" }}>
              <select
                value={selectedBlueprintId || ""}
                onChange={(e) => {
                  const val = e.target.value ? Number(e.target.value) : null;
                  setSelectedBlueprintId(val);
                  if (val) setSelectedStrategyDocId(null);
                }}
                style={{
                  width: "100%", padding: "10px 14px", borderRadius: "8px",
                  border: "1px solid #D1D5DB", fontSize: "14px", outline: "none",
                  backgroundColor: "#FFFFFF",
                }}
              >
                <option value="">No blueprint</option>
                {blueprints.map((bp) => (
                  <option key={bp.id} value={bp.id}>
                    {bp.title || `Blueprint #${bp.id}`}
                  </option>
                ))}
              </select>
              {selectedBlueprintId && (
                <a
                  href={`/blueprints/${selectedBlueprintId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: "12px", color: "#6366F1", textDecoration: "none", marginTop: "4px", display: "inline-block" }}
                >
                  Open blueprint
                </a>
              )}
            </div>
          )}
        </div>
      )}

      {/* Calendar setup */}
      <div style={{
        backgroundColor: "#F9FAFB", borderRadius: "12px", padding: "20px",
        border: "1px solid #E5E7EB", marginBottom: "32px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
          <Calendar size={18} color="#6366F1" />
          <span style={{ fontSize: "14px", fontWeight: 600, color: "#374151" }}>
            Schedule recurring session (optional)
          </span>
        </div>
        <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
          <select
            value={calendarDay}
            onChange={(e) => setCalendarDay(e.target.value)}
            style={{
              padding: "8px 12px", borderRadius: "8px", border: "1px solid #D1D5DB",
              fontSize: "14px", outline: "none",
            }}
          >
            {DAYS_OF_WEEK.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          <input
            type="time"
            value={calendarTime}
            onChange={(e) => setCalendarTime(e.target.value)}
            style={{
              padding: "8px 12px", borderRadius: "8px", border: "1px solid #D1D5DB",
              fontSize: "14px", outline: "none",
            }}
          />
          <a
            href={generateCalendarUrl()}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: "8px 16px", borderRadius: "8px",
              backgroundColor: "#FFFFFF", border: "1px solid #D1D5DB",
              fontSize: "13px", color: "#374151", textDecoration: "none",
              display: "inline-flex", alignItems: "center", gap: "6px",
            }}
          >
            <Calendar size={14} /> Add to Google Calendar
          </a>
        </div>
      </div>

      {selectedBlueprintId && (
        <p style={{ fontSize: "13px", color: "#6B7280", marginBottom: "12px" }}>
          Using blueprint: <a href={`/blueprints/${selectedBlueprintId}`} style={{ color: "#6366F1", textDecoration: "none", fontWeight: 600 }}>{blueprints.find((bp) => bp.id === selectedBlueprintId)?.title || `#${selectedBlueprintId}`}</a>
        </p>
      )}

      {loading ? (
        <div style={{
          padding: "32px", borderRadius: "12px", border: "1px solid #E5E7EB",
          backgroundColor: "#F9FAFB", textAlign: "center",
        }}>
          <div style={{
            width: "24px", height: "24px", border: "3px solid #E5E7EB",
            borderTopColor: "#6366F1", borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
            margin: "0 auto 16px",
          }} />
          <p style={{ fontSize: "16px", fontWeight: 600, color: "#111827", marginBottom: "4px" }}>
            {loadingStep || "Starting..."}
          </p>
          <p style={{ fontSize: "13px", color: "#9CA3AF", margin: 0 }}>
            Thinking deeply. This usually takes 1 to 3 minutes.
          </p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : (
        <button
          onClick={handleStart}
          style={{
            padding: "14px 32px", fontSize: "16px", fontWeight: 600,
            backgroundColor: "#6366F1", color: "#FFFFFF", border: "none",
            borderRadius: "8px", cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: "8px",
          }}
        >
          <Sparkles size={18} /> Start 15-Minute Session
        </button>
      )}
      {error && <p style={{ color: "#EF4444", marginTop: "12px", fontSize: "14px" }}>{error}</p>}

      {/* Past sessions */}
      {pastSessions.length > 0 && (
        <div style={{ marginTop: "48px" }}>
          <h2 style={{ fontSize: "20px", fontWeight: 700, marginBottom: "16px", color: "#111827" }}>
            Past Sessions
          </h2>
          {pastSessions.map((session) => {
            const answered = (session.questions || []).filter((q) => q.answer_text && !q.dismissed);
            const categories = answered.reduce<Record<string, number>>((acc, q) => {
              acc[q.category] = (acc[q.category] || 0) + 1;
              return acc;
            }, {});
            const isExpanded = expandedSession === session.id;

            return (
              <div key={session.id} style={{
                border: "1px solid #E5E7EB", borderRadius: "12px",
                marginBottom: "12px", overflow: "hidden",
              }}>
                <div
                  onClick={() => setExpandedSession(isExpanded ? null : session.id)}
                  style={{
                    padding: "16px 20px", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: "12px",
                  }}
                >
                  <span style={{ color: "#9CA3AF" }}>
                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </span>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: "14px", fontWeight: 600, color: "#111827" }}>
                      {new Date(session.created_at).toLocaleDateString("en-US", {
                        weekday: "long", month: "long", day: "numeric", year: "numeric",
                      })}
                    </span>
                    <span style={{ fontSize: "13px", color: "#6B7280", marginLeft: "12px" }}>
                      {answered.length} answer{answered.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                    {Object.entries(categories).map(([cat, count]) => (
                      <span key={cat} style={{
                        fontSize: "11px", padding: "2px 6px", borderRadius: "99px",
                        backgroundColor: "#F3F4F6", color: "#6B7280",
                      }}>
                        {cat}: {count}
                      </span>
                    ))}
                  </div>
                </div>
                {isExpanded && (
                  <div style={{ padding: "0 20px 16px", borderTop: "1px solid #F3F4F6" }}>
                    {answered.map((q, i) => (
                      <div key={i} style={{ padding: "12px 0", borderBottom: i < answered.length - 1 ? "1px solid #F3F4F6" : "none" }}>
                        <p style={{ fontSize: "13px", fontWeight: 600, color: "#374151", marginBottom: "4px" }}>
                          {q.question}
                        </p>
                        <p style={{ fontSize: "13px", color: "#6B7280", lineHeight: 1.5 }}>
                          {q.answer_text}
                        </p>
                        {q.quality_score && (
                          <span style={{
                            fontSize: "11px", marginTop: "6px", display: "inline-block",
                            padding: "2px 8px", borderRadius: "99px",
                            backgroundColor: q.quality_score === "Excellent" ? "#EEF2FF" : q.quality_score === "Good" ? "#F0FDF4" : q.quality_score === "Okay" ? "#FFFBEB" : "#FEF2F2",
                            color: q.quality_score === "Excellent" ? "#4F46E5" : q.quality_score === "Good" ? "#16A34A" : q.quality_score === "Okay" ? "#D97706" : "#DC2626",
                          }}>
                            {q.quality_score}{q.quality_feedback ? ` — ${q.quality_feedback}` : ""}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderQuestions = () => (
    <div style={{ maxWidth: "840px", margin: "0 auto", padding: "40px 20px" }}>
      <div className="si-questions-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
        <h2 style={{ fontSize: "24px", fontWeight: 700, color: "#111827", margin: 0 }}>
          Your Questions
        </h2>
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={() => setShowAddTopic(!showAddTopic)}
            style={{
              padding: "8px 20px", borderRadius: "8px",
              border: "1px solid #D1D5DB", backgroundColor: showAddTopic ? "#F3F4F6" : "#FFFFFF",
              fontSize: "13px", color: "#374151", cursor: "pointer",
              display: "flex", alignItems: "center", gap: "6px",
              whiteSpace: "nowrap",
            }}
          >
            <Plus size={14} /> Add Your Own Topic
          </button>
          <button
            onClick={handleRefreshAll}
            disabled={loading}
            style={{
              padding: "8px 20px", borderRadius: "8px",
              border: "1px solid #D1D5DB", backgroundColor: "#FFFFFF",
              fontSize: "13px", color: "#374151", cursor: loading ? "wait" : "pointer",
              display: "flex", alignItems: "center", gap: "6px",
              whiteSpace: "nowrap",
            }}
          >
            <RefreshCw size={14} /> Refresh All
          </button>
        </div>
      </div>

      <p style={{ fontSize: "14px", color: "#6B7280", marginBottom: "20px" }}>
        {answeredCount} of {activeCount} answered — answer at least a few, then complete your session to send the results to our team for polishing.
        {" "}<a href="/content-interviews?highlight=professional" style={{ color: "#6366F1", textDecoration: "none" }}>Prefer a human? Book a call instead.</a>
      </p>

      {/* Add custom topic (expandable) */}
      {showAddTopic && (
        <div style={{
          display: "flex", gap: "8px", marginBottom: "24px",
          padding: "16px", backgroundColor: "#F9FAFB", borderRadius: "12px",
          border: "1px solid #E5E7EB",
        }}>
          <input
            type="text"
            value={customTopicInput}
            onChange={(e) => setCustomTopicInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddCustomTopic()}
            placeholder="Add your own topic..."
            autoFocus
            style={{
              flex: 1, padding: "10px 14px", borderRadius: "8px",
              border: "1px solid #D1D5DB", fontSize: "14px", outline: "none",
            }}
          />
          {browserSupportsSpeechRecognition && (
            <button
              onClick={() => {
                if (listening) {
                  SpeechRecognition.stopListening();
                  setTopicMicActive(false);
                } else {
                  resetTranscript();
                  setTopicMicActive(true);
                  SpeechRecognition.startListening({ continuous: true });
                }
              }}
              style={{
                padding: "8px 12px", borderRadius: "8px",
                border: `1px solid ${listening ? "#EF4444" : "#D1D5DB"}`,
                backgroundColor: listening ? "#FEF2F2" : "#FFFFFF",
                cursor: "pointer",
              }}
            >
              {listening ? <MicOff size={16} color="#EF4444" /> : <Mic size={16} color="#6366F1" />}
            </button>
          )}
          <button
            onClick={handleAddCustomTopic}
            disabled={addingTopic || !customTopicInput.trim()}
            style={{
              padding: "8px 16px", borderRadius: "8px",
              backgroundColor: "#FFFFFF", color: "#374151", border: "1px solid #D1D5DB",
              fontSize: "13px", fontWeight: 600,
              cursor: addingTopic || !customTopicInput.trim() ? "not-allowed" : "pointer",
              opacity: addingTopic || !customTopicInput.trim() ? 0.6 : 1,
              display: "flex", alignItems: "center", gap: "6px",
            }}
          >
            <Plus size={14} /> {addingTopic ? "Adding..." : "Add"}
          </button>
        </div>
      )}

      <p style={{ fontSize: "13px", color: "#9CA3AF", marginBottom: "20px" }}>
        Use the arrows to browse more questions in each category. Answer as many as you like.
      </p>

      {/* Pillar sections */}
      {([
        { key: "custom", label: "Custom" },
        { key: "business", label: "Your Business" },
        { key: "news", label: "News" },
        { key: "personal", label: "Personal" },
        { key: "industry", label: "Industry" },
      ] as { key: string; label: string }[]).map((pillar) => {
        const pillarQuestions = questions
          .map((q, i) => ({ ...q, originalIndex: i }))
          .filter((q) => (q.pillar || q.category) === pillar.key && !q.dismissed);
        if (pillarQuestions.length === 0 && pillar.key !== "news") return null;

        if (pillarQuestions.length === 0 && pillar.key === "news") {
          return (
            <div key={pillar.key} style={{ marginBottom: "28px" }}>
              <h3 style={{ fontSize: "16px", fontWeight: 700, color: "#111827", marginBottom: "8px" }}>News</h3>
              <div style={{
                padding: "20px", borderRadius: "12px", border: "1px solid #E5E7EB",
                backgroundColor: "#F9FAFB", textAlign: "center",
              }}>
                <p style={{ fontSize: "14px", color: "#6B7280", margin: "0 0 12px 0" }}>
                  We couldn't find recent news articles for your industry. This can happen if the search timed out.
                </p>
                <button
                  onClick={handleRetryNews}
                  disabled={retryingNews}
                  style={{
                    padding: "8px 20px", borderRadius: "8px",
                    border: "1px solid #D1D5DB", backgroundColor: "#FFFFFF",
                    fontSize: "13px", color: "#374151", cursor: retryingNews ? "wait" : "pointer",
                    display: "inline-flex", alignItems: "center", gap: "6px",
                  }}
                >
                  <RefreshCw size={14} className={retryingNews ? "spin" : ""} />
                  {retryingNews ? "Searching..." : "Retry search"}
                </button>
              </div>
            </div>
          );
        }

        const activeIdx = activePillarIndex[pillar.key] || 0;
        const clampedIdx = Math.min(activeIdx, pillarQuestions.length - 1);
        const activeQ = pillarQuestions[clampedIdx];
        const answeredInPillar = pillarQuestions.filter((q) => q.answer_text).length;

        return (
          <div key={pillar.key} style={{ marginBottom: "28px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
              <h3 style={{ fontSize: "16px", fontWeight: 700, color: "#111827", margin: 0 }}>
                {pillar.label}
              </h3>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                <span style={{ fontSize: "12px", color: "#9CA3AF" }}>
                  {answeredInPillar}/{pillarQuestions.length} answered
                </span>
                <button
                  onClick={() => setActivePillarIndex((prev) => ({ ...prev, [pillar.key]: Math.max(0, clampedIdx - 1) }))}
                  disabled={clampedIdx === 0}
                  style={{
                    padding: "4px", borderRadius: "6px", border: "1px solid #D1D5DB",
                    backgroundColor: "#FFF", cursor: clampedIdx === 0 ? "default" : "pointer",
                    opacity: clampedIdx === 0 ? 0.3 : 1, display: "flex",
                  }}
                >
                  <ChevronLeft size={16} color="#374151" />
                </button>
                <span style={{ fontSize: "12px", color: "#6B7280", minWidth: "32px", textAlign: "center" }}>
                  {clampedIdx + 1}/{pillarQuestions.length}
                </span>
                <button
                  onClick={() => setActivePillarIndex((prev) => ({ ...prev, [pillar.key]: Math.min(pillarQuestions.length - 1, clampedIdx + 1) }))}
                  disabled={clampedIdx >= pillarQuestions.length - 1}
                  style={{
                    padding: "4px", borderRadius: "6px", border: "1px solid #D1D5DB",
                    backgroundColor: "#FFF", cursor: clampedIdx >= pillarQuestions.length - 1 ? "default" : "pointer",
                    opacity: clampedIdx >= pillarQuestions.length - 1 ? 0.3 : 1, display: "flex",
                  }}
                >
                  <ChevronRight size={16} color="#374151" />
                </button>
              </div>
            </div>

            {activeQ && (
              <QuestionCard
                key={`${activeQ.originalIndex}-${activeQ.question}`}
                question={activeQ}
                index={activeQ.originalIndex}
                onDismiss={handleDismiss}
                onSwap={handleSwap}
                onAnswer={handleAnswer}
                onDone={handleDone}
                onAssess={handleAssess}
                onSharpen={handleSharpen}
                onBuildEmail={handleBuildEmail}
                isSwapping={swappingIndex === activeQ.originalIndex}
                isSharpening={sharpeningIndex === activeQ.originalIndex}
              />
            )}
          </div>
        );
      })}

      {error && <p style={{ color: "#EF4444", marginTop: "12px", fontSize: "14px" }}>{error}</p>}

      {/* Save / Complete buttons */}
      {savedNotice && (
        <p style={{ color: "#059669", fontSize: "13px", marginTop: "12px" }}>{savedNotice}</p>
      )}
      <div className="si-bottom-buttons" style={{ marginTop: "32px", display: "flex", gap: "12px" }}>
        <button
          onClick={() => setCurrentStep("setup")}
          style={{
            padding: "12px 24px", borderRadius: "8px",
            border: "1px solid #D1D5DB", backgroundColor: "#FFFFFF",
            fontSize: "14px", color: "#374151", cursor: "pointer",
            display: "flex", alignItems: "center", gap: "6px",
          }}
        >
          <ArrowLeft size={16} /> Back
        </button>
        <button
          onClick={handleSaveDraft}
          disabled={saving}
          style={{
            padding: "12px 24px", borderRadius: "8px",
            border: "1px solid #D1D5DB", backgroundColor: "#FFFFFF",
            fontSize: "14px", color: "#374151", fontWeight: 600,
            cursor: saving ? "not-allowed" : "pointer",
            opacity: saving ? 0.7 : 1,
            display: "flex", alignItems: "center", gap: "8px",
          }}
        >
          <Save size={16} /> {saving ? "Saving..." : "Save"}
        </button>
        <button
          onClick={handleCompleteSession}
          disabled={saving || answeredCount === 0}
          style={{
            padding: "12px 32px", borderRadius: "8px",
            backgroundColor: answeredCount > 0 ? "#6366F1" : "#E5E7EB",
            color: answeredCount > 0 ? "#FFFFFF" : "#9CA3AF",
            border: "none", fontSize: "14px", fontWeight: 600,
            cursor: saving || answeredCount === 0 ? "not-allowed" : "pointer",
            opacity: saving ? 0.7 : 1,
            display: "flex", alignItems: "center", gap: "8px",
          }}
        >
          <Save size={16} /> {saving ? "Completing..." : `Complete Session (${answeredCount} answers)`}
        </button>
      </div>

    </div>
  );

  const renderReview = () => {
    const answered = questions.filter((q) => q.answer_text && !q.dismissed);
    const categories = answered.reduce<Record<string, number>>((acc, q) => {
      acc[q.category] = (acc[q.category] || 0) + 1;
      return acc;
    }, {});

    return (
      <div style={{ maxWidth: "640px", margin: "0 auto", padding: "40px 20px", textAlign: "center" }}>
        <div style={{
          width: "64px", height: "64px", borderRadius: "50%",
          backgroundColor: "#F0FDF4", display: "flex", alignItems: "center",
          justifyContent: "center", margin: "0 auto 16px",
        }}>
          <Save size={28} color="#16A34A" />
        </div>
        <h2 style={{ fontSize: "24px", fontWeight: 700, color: "#111827", marginBottom: "8px" }}>
          Session Saved!
        </h2>
        {saveResult && (
          <p style={{ fontSize: "15px", color: "#6B7280", marginBottom: "24px" }}>
            Saved <strong>{saveResult.ingredients_created} answer{saveResult.ingredients_created !== 1 ? "s" : ""}</strong> as a content request.
          </p>
        )}

        <div style={{
          backgroundColor: "#F9FAFB", borderRadius: "12px", padding: "20px",
          border: "1px solid #E5E7EB", textAlign: "left", marginBottom: "32px",
        }}>
          <p style={{ fontSize: "14px", fontWeight: 600, color: "#374151", marginBottom: "12px" }}>
            Session Summary
          </p>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
            {Object.entries(categories).map(([cat, count]) => (
              <span key={cat} style={{
                fontSize: "12px", padding: "4px 10px", borderRadius: "99px",
                backgroundColor: "#EEF2FF", color: "#4338CA",
              }}>
                {cat}: {count}
              </span>
            ))}
          </div>
          {answered.map((q, i) => (
            <div key={i} style={{ padding: "8px 0", borderTop: i > 0 ? "1px solid #F3F4F6" : "none" }}>
              <p style={{ fontSize: "13px", fontWeight: 600, color: "#374151" }}>{q.question}</p>
              <p style={{ fontSize: "13px", color: "#6B7280", marginTop: "2px" }}>
                {(q.answer_text || "").substring(0, 150)}...
              </p>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
          <button
            onClick={() => {
              setQuestions([]);
              setInterview(null);
              setSaveResult(null);
              setCurrentStep("setup");
            }}
            style={{
              padding: "12px 24px", borderRadius: "8px",
              border: "1px solid #D1D5DB", backgroundColor: "#FFFFFF",
              fontSize: "14px", color: "#374151", cursor: "pointer",
            }}
          >
            Start New Session
          </button>
          <button
            onClick={() => navigate("/requests/list")}
            style={{
              padding: "12px 24px", borderRadius: "8px",
              backgroundColor: "#6366F1", color: "#FFFFFF", border: "none",
              fontSize: "14px", fontWeight: 600, cursor: "pointer",
            }}
          >
            View in Requests
          </button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <style>{`
        @media (max-width: 640px) {
          .si-questions-header { flex-direction: column; align-items: flex-start !important; gap: 12px; }
          .si-column-headers { display: none !important; }
          .si-bottom-buttons { flex-direction: column; }
          .si-bottom-buttons button { width: 100%; justify-content: center; }
        }
      `}</style>
      {currentStep === "setup" && renderSetup()}
      {currentStep === "questions" && renderQuestions()}
      {currentStep === "review" && renderReview()}
    </div>
  );
};

export default SelfInterview;
